<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { getInboxIconByType } from 'dashboard/helper/inbox';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();

const confirmDeleteContactDialogRef = ref(null);

const avatarFile = ref(null);
const avatarUrl = ref('');

const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const contactData = ref({});

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

onMounted(() => {
  Object.assign(contactData.value, getInitialContactData());
});

const createdAt = computed(() => {
  return contactData.value?.createdAt
    ? dynamicTime(contactData.value.createdAt)
    : '';
});

const CHANNEL_LABELS = {
  'Channel::WebWidget': 'Website',
  'Channel::Whatsapp': 'WhatsApp',
  'Channel::FacebookPage': 'Facebook',
  'Channel::Instagram': 'Instagram',
  'Channel::Email': 'Email',
  'Channel::Api': 'API',
  'Channel::TwilioSms': 'SMS',
  'Channel::Telegram': 'Telegram',
  'Channel::Line': 'Line',
  'Channel::Sms': 'SMS',
  'Channel::Tiktok': 'TikTok',
  'Channel::Voip': 'Voice',
};

const sourceChannel = computed(
  () => props.selectedContact?.sourceChannel || ''
);
const channelIcon = computed(() =>
  sourceChannel.value ? getInboxIconByType(sourceChannel.value) : null
);
const channelLabel = computed(
  () =>
    CHANNEL_LABELS[sourceChannel.value] ||
    sourceChannel.value?.replace('Channel::', '') ||
    ''
);

const leadCustomAttributes = computed(
  () => props.selectedContact?.customAttributes || {}
);
const stage = computed(() => leadCustomAttributes.value.stage || '');
const verificationStatus = computed(
  () => leadCustomAttributes.value.verification_status || ''
);
const junkReason = computed(() => leadCustomAttributes.value.junk_reason || '');
const verifiedBy = computed(() => leadCustomAttributes.value.verified_by || '');
const verifiedAt = computed(() => {
  const value = leadCustomAttributes.value.verified_at;
  return value ? new Date(value).toLocaleString() : '';
});

const VERIFICATION_COLORS = {
  'Pending Review': 'amber',
  Verified: 'teal',
  Junk: 'ruby',
};
const verificationColor = computed(
  () => VERIFICATION_COLORS[verificationStatus.value] || 'slate'
);
const isJunk = computed(() => verificationStatus.value === 'Junk');

const PRIORITY_LABEL_KEYS = {
  urgent: 'CONVERSATION.PRIORITY.OPTIONS.URGENT',
  high: 'CONVERSATION.PRIORITY.OPTIONS.HIGH',
  medium: 'CONVERSATION.PRIORITY.OPTIONS.MEDIUM',
  low: 'CONVERSATION.PRIORITY.OPTIONS.LOW',
};
const PRIORITY_COLORS = {
  urgent: 'ruby',
  high: 'amber',
  medium: 'blue',
  low: 'slate',
};
const priorityLabel = computed(() =>
  props.selectedContact?.priority
    ? t(PRIORITY_LABEL_KEYS[props.selectedContact.priority])
    : ''
);
const priorityColor = computed(
  () => PRIORITY_COLORS[props.selectedContact?.priority] || 'slate'
);

const assignee = computed(() => props.selectedContact?.assignee || null);
const priority = computed(() => props.selectedContact?.priority || '');
const captainSummary = computed(
  () => props.selectedContact?.captainSummary || ''
);
const lastConversationAt = computed(() => {
  return props.selectedContact?.lastConversationAt
    ? dynamicTime(props.selectedContact.lastConversationAt)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail;
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const updateContact = async () => {
  try {
    const { customAttributes, ...basicContactData } = contactData.value;
    await store.dispatch('contacts/update', basicContactData);
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

const isEditingJunkReason = ref(false);
const junkReasonDraft = ref('');
const isSavingJunkReason = ref(false);

const startEditingJunkReason = () => {
  junkReasonDraft.value = junkReason.value;
  isEditingJunkReason.value = true;
};

const cancelEditingJunkReason = () => {
  isEditingJunkReason.value = false;
};

const saveJunkReason = async () => {
  isSavingJunkReason.value = true;
  try {
    await store.dispatch('contacts/update', {
      id: props.selectedContact.id,
      customAttributes: { junk_reason: junkReasonDraft.value },
    });
    isEditingJunkReason.value = false;
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  } finally {
    isSavingJunkReason.value = false;
  }
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact && props.selectedContact.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="flex flex-col items-start gap-8 pb-6">
    <!-- SECTION 1: OVERVIEW -->
    <div class="flex flex-col items-start w-full gap-3">
      <Avatar
        :src="avatarSrc || ''"
        :name="selectedContact?.name || ''"
        :size="72"
        allow-upload
        @upload="handleAvatarUpload"
        @delete="handleAvatarDelete"
      />
      <div class="flex flex-col gap-1">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ selectedContact?.name }}
          </h3>
          <span
            v-if="selectedContact?.additionalAttributes?.companyName"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-ph-building-light size-4 text-n-slate-10" />
            {{ selectedContact.additionalAttributes.companyName }}
          </span>
        </div>
        <span
          v-if="selectedContact?.identifier"
          class="inline-flex items-center gap-1 text-sm text-n-slate-11"
        >
          <span class="i-ph-user-gear text-n-slate-10 size-4" />
          {{ selectedContact?.identifier }}
        </span>
      </div>
      <ContactLabels :contact-id="selectedContact?.id" />
      <div class="flex flex-wrap items-center gap-1.5">
        <Label v-if="sourceChannel" :label="channelLabel" color="slate" compact>
          <template #icon>
            <Icon :icon="channelIcon" class="size-3.5" />
          </template>
        </Label>
        <Label
          v-if="priority"
          :label="priorityLabel"
          :color="priorityColor"
          compact
        >
          <template #icon>
            <CardPriorityIcon :priority="priority" class="!size-3.5" />
          </template>
        </Label>
        <Label v-if="stage" :label="stage" color="iris" compact />
        <Label
          v-if="verificationStatus"
          :label="verificationStatus"
          :color="verificationColor"
          compact
        />
      </div>
    </div>

    <!-- SECTION 2: CAPTAIN SUMMARY -->
    <div class="flex flex-col items-start w-full gap-2">
      <h6 class="text-base font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.DETAILS.CAPTAIN_SUMMARY.TITLE') }}
      </h6>
      <CardLayout class="w-full">
        <p
          v-if="captainSummary"
          class="text-sm text-n-slate-11 whitespace-pre-line"
        >
          {{ captainSummary }}
        </p>
        <p v-else class="text-sm text-n-slate-10 italic">
          {{ t('CONTACTS_LAYOUT.DETAILS.CAPTAIN_SUMMARY.EMPTY') }}
        </p>
      </CardLayout>
    </div>

    <!-- SECTION 3: CUSTOMER INFORMATION -->
    <div
      class="flex flex-col items-start w-full gap-6 pt-6 border-t border-n-strong"
    >
      <h6 class="text-base font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.DETAILS.CONTACT_INFORMATION') }}
      </h6>
      <ContactsForm
        ref="contactsFormRef"
        :contact-data="contactData"
        is-details-view
        @update="handleFormUpdate"
      />
      <Button
        :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="updateContact"
      />
    </div>

    <!-- SECTION 4: WORKFLOW (read-only, editing via Attributes tab) -->
    <div
      class="flex flex-col items-start w-full gap-3 pt-6 border-t border-n-strong"
    >
      <h6 class="text-base font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.DETAILS.WORKFLOW.TITLE') }}
      </h6>
      <div class="flex flex-col w-full gap-2.5">
        <div v-if="stage" class="flex items-center gap-2 text-sm">
          <span class="w-40 text-n-slate-10">
            {{ t('CONTACTS_LAYOUT.DETAILS.LEAD_INFORMATION.STAGE') }}
          </span>
          <Label :label="stage" color="iris" compact />
        </div>
        <div v-if="verificationStatus" class="flex items-center gap-2 text-sm">
          <span class="w-40 text-n-slate-10">
            {{
              t('CONTACTS_LAYOUT.DETAILS.LEAD_INFORMATION.VERIFICATION_STATUS')
            }}
          </span>
          <Label
            :label="verificationStatus"
            :color="verificationColor"
            compact
          />
        </div>
        <div v-if="assignee" class="flex items-center gap-2 text-sm">
          <span class="w-40 text-n-slate-10">
            {{ t('CONTACTS_LAYOUT.DETAILS.LEAD_INFORMATION.ASSIGNED_AGENT') }}
          </span>
          <span class="inline-flex items-center gap-1.5 text-n-slate-12">
            <Avatar
              :name="assignee.name"
              :src="assignee.thumbnail"
              :size="16"
            />
            {{ assignee.name }}
          </span>
        </div>
        <div v-if="createdAt" class="flex items-center gap-2 text-sm">
          <span class="w-40 text-n-slate-10">
            {{ t('CONTACTS_LAYOUT.DETAILS.LEAD_INFORMATION.CREATED_DATE') }}
          </span>
          <span class="text-n-slate-12">{{ createdAt }}</span>
        </div>
        <div v-if="lastConversationAt" class="flex items-center gap-2 text-sm">
          <span class="w-40 text-n-slate-10">
            {{
              t('CONTACTS_LAYOUT.DETAILS.LEAD_INFORMATION.LAST_CONVERSATION')
            }}
          </span>
          <span class="text-n-slate-12">{{ lastConversationAt }}</span>
        </div>
      </div>
    </div>

    <!-- SECTION 5: VERIFICATION -->
    <div
      v-if="verificationStatus"
      class="flex flex-col items-start w-full gap-3 pt-6 border-t border-n-strong"
    >
      <h6 class="text-base font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.DETAILS.VERIFICATION.TITLE') }}
      </h6>
      <div class="flex flex-col w-full gap-2.5">
        <div class="flex items-center gap-2 text-sm">
          <span class="w-40 text-n-slate-10">
            {{
              t('CONTACTS_LAYOUT.DETAILS.LEAD_INFORMATION.VERIFICATION_STATUS')
            }}
          </span>
          <Label
            :label="verificationStatus"
            :color="verificationColor"
            compact
          />
        </div>
        <div v-if="verifiedBy" class="flex items-center gap-2 text-sm">
          <span class="w-40 text-n-slate-10">
            {{ t('CONTACTS_LAYOUT.DETAILS.VERIFICATION.VERIFIED_BY') }}
          </span>
          <span class="text-n-slate-12">{{ verifiedBy }}</span>
        </div>
        <div v-if="verifiedAt" class="flex items-center gap-2 text-sm">
          <span class="w-40 text-n-slate-10">
            {{ t('CONTACTS_LAYOUT.DETAILS.VERIFICATION.VERIFIED_DATE') }}
          </span>
          <span class="text-n-slate-12">{{ verifiedAt }}</span>
        </div>
      </div>
      <div v-if="isJunk" class="flex flex-col items-start w-full gap-2">
        <div class="flex items-center justify-between w-full">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.DETAILS.VERIFICATION.JUNK_REASON_TITLE') }}
          </span>
          <Button
            v-if="!isEditingJunkReason"
            :label="
              junkReason
                ? t('CONTACTS_LAYOUT.DETAILS.VERIFICATION.EDIT_NOTE')
                : t('CONTACTS_LAYOUT.DETAILS.VERIFICATION.ADD_NOTE')
            "
            icon="i-lucide-pencil"
            variant="faded"
            color="slate"
            size="xs"
            @click="startEditingJunkReason"
          />
        </div>
        <CardLayout class="w-full">
          <template v-if="isEditingJunkReason">
            <TextArea
              v-model="junkReasonDraft"
              :placeholder="
                t('CONTACTS_LAYOUT.DETAILS.VERIFICATION.NOTE_PLACEHOLDER')
              "
              auto-height
              show-character-count
              :max-length="1000"
              min-height="6rem"
              autofocus
            />
            <div class="flex items-center gap-2 mt-2">
              <Button
                :label="t('CONTACTS_LAYOUT.DETAILS.VERIFICATION.SAVE_NOTE')"
                size="xs"
                :is-loading="isSavingJunkReason"
                @click="saveJunkReason"
              />
              <Button
                :label="t('CONTACTS_LAYOUT.DETAILS.VERIFICATION.CANCEL_NOTE')"
                variant="faded"
                color="slate"
                size="xs"
                :disabled="isSavingJunkReason"
                @click="cancelEditingJunkReason"
              />
            </div>
          </template>
          <template v-else>
            <p
              v-if="junkReason"
              class="text-sm text-n-slate-11 whitespace-pre-line"
            >
              {{ junkReason }}
            </p>
            <p v-else class="text-sm text-n-slate-10 italic">
              {{ t('CONTACTS_LAYOUT.DETAILS.VERIFICATION.NO_REASON') }}
            </p>
          </template>
        </CardLayout>
      </div>
    </div>

    <Policy :permissions="['administrator']">
      <div
        class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
      >
        <div class="flex flex-col gap-2">
          <h6 class="text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </h6>
          <span class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
          </span>
        </div>
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')"
          color="ruby"
          @click="openConfirmDeleteContactDialog"
        />
      </div>
      <ConfirmContactDeleteDialog
        ref="confirmDeleteContactDialogRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')"
      />
    </Policy>
  </div>
</template>
